import type { OneOffSessionEpisode } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface ContextBarProps {
  episodes: OneOffSessionEpisode[];
  includedIndices: Set<number>;
  onToggleInclude: (index: number) => void;
  modelContextLimit: number;
  actualInputTokens?: number;
}

function formatTokens(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(0)}k`;
  }
  return `${count}`;
}

export default function ContextBar({
  episodes,
  includedIndices,
  onToggleInclude,
  modelContextLimit,
  actualInputTokens,
}: ContextBarProps) {
  const readyEpisodes = episodes.filter((e) => e.status === "ready");

  const estimatedTokens = readyEpisodes
    .filter((e) => includedIndices.has(e.index))
    .reduce((sum, e) => sum + (e.token_count ?? 0), 0);

  // Use actual tokens from response if available, otherwise use estimate
  const displayTokens = actualInputTokens ?? estimatedTokens;
  const usagePercent = Math.min((displayTokens / modelContextLimit) * 100, 100);
  const isOverLimit = displayTokens > modelContextLimit;

  return (
    <div
      className={cn(
        "p-4 rounded-xl mb-4",
        theme.borders.neubrutalism,
        "bg-gray-50 dark:bg-gray-800/50"
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Context
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {readyEpisodes.map((episode) => {
              const isIncluded = includedIndices.has(episode.index);
              const tokens = episode.token_count ?? 0;

              return (
                <button
                  key={episode.index}
                  type="button"
                  onClick={() => onToggleInclude(episode.index)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                    "border-2",
                    isIncluded
                      ? cn(
                          "bg-primary-orange dark:bg-primary-orange-dark",
                          "border-gray-900 dark:border-gray-300",
                          "text-gray-900 dark:text-white"
                        )
                      : cn(
                          "bg-transparent",
                          "border-gray-300 dark:border-gray-600",
                          "text-gray-500 dark:text-gray-400",
                          "hover:border-gray-400 dark:hover:border-gray-500"
                        )
                  )}
                >
                  <span className="truncate max-w-[150px] inline-block align-bottom">
                    {episode.title.length > 20
                      ? episode.title.slice(0, 20) + "..."
                      : episode.title}
                  </span>
                  <span className="ml-1.5 opacity-70">({formatTokens(tokens)})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Token usage indicator */}
        <div className="flex items-center gap-3 sm:ml-4">
          <div className="text-right">
            <span
              className={cn(
                "text-sm font-semibold",
                isOverLimit
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-700 dark:text-gray-300"
              )}
            >
              {formatTokens(displayTokens)}
            </span>
            <span className="text-sm text-gray-400 dark:text-gray-500">
              /{formatTokens(modelContextLimit)}
            </span>
          </div>

          {/* Mini progress bar */}
          <div className="w-16 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className={cn(
                "h-full transition-all duration-300",
                isOverLimit
                  ? "bg-red-500"
                  : usagePercent > 80
                    ? "bg-amber-500"
                    : "bg-primary-orange dark:bg-primary-orange-dark"
              )}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>
      </div>

      {isOverLimit && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          Token limit exceeded. Deselect some episodes to continue.
        </p>
      )}
    </div>
  );
}
